<template>
  <v-container fluid class="pa-4 bg-light-soft min-h-screen">
    <!-- Header Section -->
    <v-row no-gutters class="mb-6 align-center">
      <v-col cols="auto">
        <v-btn @click="exit" depressed class="rounded-lg text-capitalize px-4 mr-4" color="white">
          <v-icon left size="20">mdi-arrow-left</v-icon>
          Volver
        </v-btn>
      </v-col>
      <v-col>
        <div class="d-flex align-center">
          <h1 class="text-h5 font-weight-black grey--text text--darken-4 mb-0 mr-4">
            {{ historyTitle }}
          </h1>
          <v-chip outlined small class="font-weight-bold" :color="getStatusColor(item.status_id)">
            {{ item.status_description || item.status_name || 'Estado Desconocido' }}
          </v-chip>
        </div>
        <div class="text-caption grey--text font-weight-medium mt-1">
          Referencia Control: <span class="primary--text font-weight-black">#REQ-{{ filLeft(item.id?.toString() || '',
            6, '0') }}</span>
        </div>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="12" md="3">
        <v-autocomplete :items="applicantsFilter" v-model="item.requested_by_id" color="primary" label="Solicitante"
          outlined dense hide-details item-value="id" item-text="employee_fullname" :readonly="!can_edit"
          class="rounded-lg bg-white">
          <template v-slot:prepend-inner>
            <v-icon small color="primary">mdi-account-circle-outline</v-icon>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <v-row>
      <!-- Items Card -->
      <v-col cols="12" md="8">
        <v-card class="rounded-xl border-soft shadow-sm overflow-hidden" elevation="0">
          <v-toolbar flat color="white" class="px-2">
            <v-icon color="primary" class="mr-2">mdi-cart-outline</v-icon>
            <span class="text-overline font-weight-black">Artículos Solicitados</span>
            <v-chip x-small class="ml-2" color="primary lighten-5" text-color="primary">
              {{ item_list.length }} ítems
            </v-chip>
            <v-chip v-if="estimatedTotal.currency" x-small class="ml-2" color="green lighten-5" text-color="green darken-3">
              Total estimado: {{ estimatedTotal.currency }} {{ currencyFormat(estimatedTotal.amount) }}
              <span v-if="estimatedTotal.withoutPrice" class="ml-1">({{ estimatedTotal.withoutPrice }} sin precio)</span>
            </v-chip>
            <v-spacer></v-spacer>
            <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Buscar en la lista..." hide-details
              outlined dense class="max-w-300 rounded-lg bg-light-soft mr-4"></v-text-field>
            <v-btn color="primary" depressed class="rounded-lg text-capitalize" v-if="can_edit"
              @click="openDialogAddItem()">
              <v-icon left>mdi-plus</v-icon>
              Agregar Ítem
            </v-btn>
          </v-toolbar>

          <v-divider></v-divider>

          <v-data-table :headers="articleHeaders" :search="search" :items="item_list" class="custom-table"
            hide-default-footer :items-per-page="-1" :item-class="rowClass">
            <template v-slot:[`item.item_id`]="{ item }">
              <span class="text-caption font-weight-black grey--text">{{ item.item_id }}</span>
            </template>

            <template v-slot:[`item.item_name`]="{ item }">
              <div class="d-flex align-center py-2">
                <v-avatar size="32" :color="isInvalidItem(item.item_id) ? 'error lighten-4' : 'grey lighten-4'" class="mr-3 rounded-lg border">
                  <v-icon small :color="isInvalidItem(item.item_id) ? 'error' : 'grey darken-2'">
                    {{ isInvalidItem(item.item_id) ? 'mdi-alert-circle' : 'mdi-package-variant' }}
                  </v-icon>
                </v-avatar>
                <div class="d-flex flex-column">
                  <span class="font-weight-black text-subtitle-2 line-height-1 mb-1"
                    :class="isInvalidItem(item.item_id) ? 'error--text' : 'grey--text text--darken-4'">
                    {{ item.item_name }}
                  </span>
                  <span class="text-caption font-weight-medium"
                    :class="isInvalidItem(item.item_id) ? 'error--text' : 'grey--text'">SKU: {{ item.item_id }}</span>
                  <v-chip v-if="isInvalidItem(item.item_id)" x-small color="error" outlined class="mt-1" style="max-width: fit-content;">
                    <v-icon x-small left>mdi-alert</v-icon>
                    No existe en el catálogo destino
                  </v-chip>
                </div>
              </div>
            </template>

            <template v-slot:[`item.unit_name`]="{ item }">
              <v-chip x-small outlined label class="font-weight-bold grey--text">
                {{ item.unit_name }}
              </v-chip>
            </template>

            <template v-slot:[`item.requested_quantity`]="{ item }">
              <v-text-field v-model="item.requested_quantity" dense outlined hide-details type="number" min="1"
                :readonly="!can_edit" class="qty-field rounded-lg mt-1 mb-1" suffix="U"></v-text-field>
            </template>

            <template v-slot:[`item.orders`]="{ item }">
              <div class="py-2" style="min-width: 180px">
                <div v-for="order in item.orders" :key="order.order_id" class="mb-1">
                  <v-chip x-small label link :color="orderChipColor(order.status_id)" text-color="white"
                    :class="{ 'text-decoration-line-through': order.status_id === ORDER_STATUS_CANCELLED }"
                    @click="openOrder(order.order_id)">
                    #{{ order.order_id }} · {{ quantityFormat(order.quantity) }} U · {{ order.status_name }}
                  </v-chip>
                </div>
                <div v-if="item.orders && item.orders.length" class="text-caption mt-1">
                  <span class="green--text text--darken-2 font-weight-bold">Ord: {{ quantityFormat(lineProgress(item).ordered) }}</span>
                  <span v-if="lineProgress(item).rejected > 0" class="red--text text--darken-2 font-weight-bold ml-2">
                    Rech: {{ quantityFormat(lineProgress(item).rejected) }}</span>
                  <span v-if="lineProgress(item).pending > 0" class="orange--text text--darken-3 font-weight-bold ml-2">
                    Pend: {{ quantityFormat(lineProgress(item).pending) }}</span>
                </div>
                <span v-else class="text-caption grey--text">Sin órdenes</span>
              </div>
            </template>

            <template v-slot:[`item.price`]="{ item }">
              <v-progress-circular v-if="loadingPrices && !(item.item_id in lastPrices)" indeterminate size="16"
                width="2" color="grey"></v-progress-circular>
              <v-tooltip v-else-if="lastPrices[item.item_id]" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span class="font-weight-medium grey--text text--darken-3" v-bind="attrs" v-on="on">
                    {{ lastPrices[item.item_id].currency_code }} {{ currencyFormat(lastPrices[item.item_id].price) }}
                  </span>
                </template>
                <div class="text-caption">
                  <div class="font-weight-bold">Último precio de compra</div>
                  <div>
                    {{ lastPrices[item.item_id].original_currency_code }}
                    {{ currencyFormat(lastPrices[item.item_id].original_price) }}
                    <span v-if="lastPrices[item.item_id].original_currency_code !== lastPrices[item.item_id].currency_code">
                      (convertido a tasa de hoy)</span>
                  </div>
                  <div>Orden #{{ lastPrices[item.item_id].order_id }} · {{ dateOnly(lastPrices[item.item_id].order_date) }}</div>
                  <div v-if="lastPrices[item.item_id].supplier">{{ lastPrices[item.item_id].supplier }}</div>
                </div>
              </v-tooltip>
              <span v-else class="text-caption grey--text">Sin compras</span>
            </template>

            <template v-slot:[`item.amount`]="{ item }">
              <span v-if="lastPrices[item.item_id]" class="font-weight-black primary--text">
                {{ lastPrices[item.item_id].currency_code }} {{ currencyFormat(lineAmount(item)) }}
              </span>
              <span v-else class="text-caption grey--text">—</span>
            </template>

            <template v-slot:[`item.remove`]="{ item }">
              <v-btn icon small color="error lighten-4" @click="removeItem(item)" v-if="can_edit">
                <v-icon small color="error">mdi-trash-can-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>

          <v-divider></v-divider>

          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-4">
              <v-icon small color="grey lighten-1 " class="mr-2">mdi-pencil-outline</v-icon>
              <span class="text-overline grey--text font-weight-black">Notas / Especificaciones</span>
            </div>
            <v-textarea v-model="item.note"
              placeholder="Escribe aquí cualquier observación o detalle adicional sobre el pedido..." rows="3" outlined
              dense hide-details class="rounded-xl bg-light-soft" :readonly="!can_edit"></v-textarea>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-6">
            <v-spacer></v-spacer>
            <div class="d-flex gap-3">
              <v-btn color="success" min-width="150" depressed class="rounded-lg text-capitalize px-6" @click="save(1)"
                v-if="can_edit" :disabled="disableSave || isSaving" :loading="isSaving">
                <v-icon left>mdi-content-save-outline</v-icon>
                Guardar Borrador
              </v-btn>
              <v-btn color="primary" min-width="150" depressed class="rounded-lg text-capitalize px-6" @click="save(2)"
                v-if="can_edit" :disabled="disableSave || isSaving" :loading="isSaving">
                <v-icon left>mdi-send-outline</v-icon>
                Enviar Solicitud
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </v-col>

      <!-- History Column -->
      <v-col cols="12" md="4">
        <v-card class="rounded-xl border-soft shadow-sm h-full bg-white" elevation="0">
          <v-toolbar flat color="white" class="px-4 pt-2">
            <v-avatar color="primary lighten-5" size="32" class="mr-3">
              <v-icon color="primary" small>mdi-history</v-icon>
            </v-avatar>
            <span class="text-subtitle-1 font-weight-black grey--text text--darken-4">Histórico</span>
            <v-spacer></v-spacer>
            <v-chip x-small class="font-weight-black" color="grey lighten-4">{{ time_line_status.length }}
              eventos</v-chip>
          </v-toolbar>

          <v-card-text class="pa-6">
            <v-timeline dense class="modern-timeline-refined pt-0">
              <v-timeline-item v-for="(historyItem, idx) in time_line_status" :key="idx" small fill-dot
                :color="time_line_config(2, historyItem.item_request_status_id).color" class="pb-8">
                <template v-slot:icon v-if="time_line_config(2, historyItem.item_request_status_id).icon">
                  <v-icon x-small color="white">{{ time_line_config(2, historyItem.item_request_status_id).icon
                  }}</v-icon>
                </template>

                <div class="timeline-bubble pa-4 rounded-xl position-relative border"
                  :style="{ borderLeft: `4px solid ${getColorCode(historyItem.item_request_status_id)}` }">
                  <div class="d-flex justify-space-between align-start mb-2">
                    <div class="d-flex flex-column">
                      <span class="text-caption font-weight-black text-uppercase letter-spacing-1 mb-1"
                        :style="{ color: getColorCode(historyItem.item_request_status_id) }">
                        {{ historyItem.name }}
                      </span>
                      <div class="d-flex align-center">
                        <v-icon x-small color="grey lighten-1 " class="mr-1">mdi-clock-outline</v-icon>
                        <span class="text-tiny grey--text font-weight-bold">{{ dateFormated(historyItem.created_date)
                        }}</span>
                      </div>
                    </div>
                    <v-avatar size="24" color="white" class="border shadow-xs">
                      <span class="text-tiny font-weight-black primary--text">{{ getInitials(historyItem.use_fullname)
                      }}</span>
                    </v-avatar>
                  </div>

                  <div class="d-flex align-center mt-3 pt-2 border-top-light">
                    <span class="text-caption grey--text text--darken-2 font-weight-medium text-truncate">
                      {{ historyItem.use_fullname }}
                    </span>
                  </div>

                  <div v-if="historyItem.order_id" class="mt-3">
                    <v-btn small depressed color="primary lighten-5" block
                      class="rounded-lg text-capitalize font-weight-bold primary--text"
                      @click="openOrder(historyItem.order_id)">
                      <v-icon small left>mdi-file-eye-outline</v-icon>
                      Orden #{{ filLeft(historyItem.order_id.toString(), 12, "0") }}
                    </v-btn>
                  </div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialogs -->
    <v-dialog width="95%" height="98vh" v-model="dialogAddItem" overlay-opacity="0.9">
      <articles_catalogue v-if="dialogAddItem && location && location.id && location.bus_id" :type="1"
        :currencie_rate="'1'" :selected_currencies="1" :itbis="[]" :removeItemSelected="false"
        :search_in_requests="false" @selected-item="addItem($event)" :data="{
          location: location.id,
          business: location.bus_id,
          warehouse: warehouse?.id || 0,
          catalogue: location.catalogue_id || 0,
        }" @close-item="dialogAddItem = false" />
      <v-card v-else-if="dialogAddItem">
        <v-card-text class="pa-4 text-center">
          <v-icon color="warning" large>mdi-alert</v-icon>
          <div class="text-h6 mt-2">Información incompleta</div>
          <p>No se puede abrir el catálogo porque no se ha seleccionado una ubicación válida.</p>
          <v-btn color="primary" @click="dialogAddItem = false">Cerrar</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";

const ORDER_STATUS_CANCELLED = 11;
const ORDER_STATUS_REJECTED = 12;
import dialog_Articles from "@/components/Inventory/CatalogeForAdjustment.vue";
import articles_catalogue from "../Inventory/ArticlesWarehouse.vue";
export default {
  data: () => ({
    ORDER_STATUS_CANCELLED,
    search: "",
    headersArticle: [
      // {
      //   align: "start",
      //   value: "item_image",
      // },
      { text: "ID", value: "item_id" },
      { text: "Articulo", value: "item_name" },
      { text: "Unidad", value: "unit_name" },
      { text: "Precio estimado", value: "price" },
      {
        text: "Cantidad solicitada",
        value: "requested_quantity",
        width: "20%",
        align: "center",
      },
      { text: "Total estimado", value: "amount" },
      { text: "", value: "remove" },
    ],
    dialogAddItem: false,
    // Último precio de compra por item_id (null = sin compras)
    lastPrices: {},
    loadingPrices: false,
    locationFrom: null,
    warehouseFrom: null,
    isSaving: false,
  }),
  name: "ItemsRequestDetails",
  props: {
    can_edit: {
      type: Boolean,
      default: false,
    },
    time_line_status: {
      type: Array,
      default() {
        return [{ name: "Borrador", item_request_status_id: 1 }];
      },
    },
    item_list: {
      type: Array,
      default() {
        return [];
      },
    },
    item: {
      type: Object,
      default() {
        return {
          status: null,
          location_from: null,
          warehouse_from: null,
        };
      },
    },
    location: {
      type: Object,
      default() {
        return {
          id: null,
          bus_id: null,
          catalogue_id: null,
        };
      },
    },
    warehouse: {
      type: Object,
      default() {
        return {
          id: null,
        };
      },
    },
    all_applicants: {
      type: Array,
      default() {
        return [
          {
            id: null,
            employee_fullname: null,
            access: {
              locations: [],
            },
          },
        ];
      },
    },
    invalid_item_ids: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  created() {
    //Este componente recibe la lista de articulos y el time line de su historico, dependiendo el estatus permitira editar
  },
  computed: {
    itemIdsKey() {
      return this.item_list.map((i) => i.item_id).join(",");
    },
    estimatedTotal() {
      let amount = 0;
      let currency = null;
      let withoutPrice = 0;
      for (const item of this.item_list) {
        const last = this.lastPrices[item.item_id];
        if (!last) {
          withoutPrice++;
          continue;
        }
        currency = last.currency_code;
        amount += this.lineAmount(item);
      }
      return { amount, currency, withoutPrice };
    },
    // La columna de órdenes solo aparece si algún artículo ya fue ordenado
    articleHeaders() {
      if (!this.item_list.some((i) => i.orders && i.orders.length)) {
        return this.headersArticle;
      }
      const headers = [...this.headersArticle];
      const amountIndex = headers.findIndex((h) => h.value === "amount");
      headers.splice(amountIndex, 0, { text: "Órdenes", value: "orders", sortable: false });
      return headers;
    },
    historyTitle() {
      return (
        "Lista de articulos " +
        (parseInt(this.item.status_id) == 1 ? "a solicitar" : "Solicitados")
      );
    },
    disableSave() {
      return this.item_list.length === 0 || this.invalid_item_ids.length > 0 || !this.item.requested_by_id;
    },
    applicantsFilter() {
      let applicants = this.all_applicants.filter((element) =>
        element.access.locations.includes(this.location.id)
      );
      if (applicants.length == 0) {
        applicants.push({
          employee_fullname: "Este location no tiene un solicitante registrado",
          id: 0,
        });
      }
      return applicants;
    },
  },
  watch: {
    // Cargar el último precio de compra de los artículos que aún no lo tienen
    itemIdsKey: {
      handler() {
        this.loadLastPrices();
      },
      immediate: true,
    },
  },
  methods: {
    async loadLastPrices() {
      const businessUnitId = this.location?.bus_id;
      const missing = this.item_list
        .map((i) => i.item_id)
        .filter((id) => id != null && !(id in this.lastPrices));
      if (!businessUnitId || missing.length === 0) return;

      this.loadingPrices = true;
      try {
        const request = await this.api_Post("shopping/last_purchase_prices", {
          business_unit_id: businessUnitId,
          item_ids: missing,
        });
        const data = request.status === 200 ? await request.json() : [];
        const prices = { ...this.lastPrices };
        for (const id of missing) prices[id] = null;
        for (const row of data) prices[row.item_id] = row;
        this.lastPrices = prices;
      } catch (error) {
        console.error("Error al consultar últimos precios de compra:", error);
      } finally {
        this.loadingPrices = false;
      }
    },
    lineAmount(item) {
      const last = this.lastPrices[item.item_id];
      return last ? parseFloat(last.price) * (parseFloat(item.requested_quantity) || 0) : 0;
    },
    dateOnly(date) {
      return moment(date).format("DD/MM/YYYY");
    },
    // Abre la orden en una pestaña nueva (misma ruta que showOrder(id, false))
    openOrder(order_id) {
      const route = this.$router.resolve({
        name: "ViewOrders",
        params: { id: order_id, type: "adsa", typeb: 0 },
      });
      window.open(route.href, "_blank");
    },
    // Mismo cálculo que el % ordenado de la lista (tope = cantidad solicitada)
    lineProgress(item) {
      const requested = parseFloat(item.requested_quantity) || 0;
      const sum = (statusFilter) =>
        (item.orders || [])
          .filter((o) => statusFilter(o.status_id))
          .reduce((acc, o) => acc + (parseFloat(o.quantity) || 0), 0);
      const ordered = Math.min(
        requested,
        sum((id) => id !== ORDER_STATUS_CANCELLED && id !== ORDER_STATUS_REJECTED)
      );
      const rejected = Math.min(requested - ordered, sum((id) => id === ORDER_STATUS_REJECTED));
      return { ordered, rejected, pending: Math.max(requested - ordered - rejected, 0) };
    },
    orderChipColor(status_id) {
      if (status_id === ORDER_STATUS_CANCELLED) return "grey";
      if (status_id === ORDER_STATUS_REJECTED) return "red darken-2";
      return "primary";
    },
    quantityFormat(value) {
      return parseFloat(value || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
    },
    getColorCode(status_id) {
      const colors = {
        1: "#94a3b8", // Borrador
        2: "#1976d2", // Pendiente
        3: "#f44336", // Cancelada
        4: "#4caf50", // Procesada
        5: "#00897b", // Actualizada
        6: "#616161", // Cerrado
        7: "#2e7d32", // Completada (calculado)
        8: "#c62828", // Rechazada (calculado)
      };
      return colors[status_id] || "#94a3b8";
    },
    currencyFormat(value) {
      if (!value) return "0.00";
      return parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getStatusColor(status_id) {
      const colors = {
        1: "grey",
        2: "primary",
        3: "error",
        4: "success",
        5: "teal darken-1",
        6: "grey darken-2",
        7: "green darken-3",
        8: "red darken-3",
      };
      return colors[status_id] || "grey";
    },
    getInitials(name) {
      if (!name) return "??";
      return name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    time_line_config(type, status_id) {
      const configs = {
        1: { color: "grey", icon: "mdi-pencil" },
        2: { color: "primary", icon: "mdi-send" },
        3: { color: "error", icon: "mdi-close-circle" },
        4: { color: "success", icon: "mdi-check-decagram" },
        5: { color: "teal darken-1", icon: "mdi-cart-arrow-right" },
        6: { color: "grey darken-2", icon: "mdi-lock" },
      };
      return configs[status_id] || { color: "grey", icon: "mdi-help-circle" };
    },
    filLeft(str, len, char) {
      return str.padStart(len, char);
    },
    dateFormated(date) {
      return moment(date).format("DD/MM/YYYY h:mm a");
    },
    openDialogAddItem() {
      this.dialogAddItem = true;
    },
    addItem(item) {
      let existArticle = this.item_list.find(
        (element) => element.item_id == item.item_id
      );
      if (existArticle != undefined) {
        this.$toast.warning("Este articulo ya se encuentra en la lista!");
        return;
      }

      this.item_list.push({
        item_id: item.item_id,
        item_name: item.item_name,
        requested_quantity: 1,
        unit_id: item.unit_id,
        unit_name: item.unit_name,
        price: item.price,
        item_image: item.item_image,
      });
    },
    removeItem(item) {
      let index = this.item_list.indexOf(item);
      this.item_list.splice(index, 1);
    },
    save(type) {
      if (this.isSaving) return;
      if (this.item_list.length == 0) {
        this.$toast.warning("Debe agregar al menos un articulo a la solicitud!");
        return;
      }
      for (let item of this.item_list) {
        if (parseInt(item.requested_quantity) < 1) {
          this.$toast.warning(`No es posible solicitar 0: ${item.item_name}!`);
          return;
        }
      }
      if (this.item.requested_by_id == 0) {
        this.$toast.warning("Debe escoger un solicitante valido!");
        return;
      }

      this.isSaving = true;
      this.$emit("save_request", {
        request_id: this.item.id,
        saveType: type, //1=borrador,2=pendiente
        applicant: this.item.requested_by_id,
        note: this.item.note,
        done: () => { this.isSaving = false; },
      });
    },
    isInvalidItem(item_id) {
      return this.invalid_item_ids.includes(item_id);
    },
    rowClass(item) {
      return this.isInvalidItem(item.item_id) ? 'invalid-item-row' : '';
    },
    exit() {
      this.$emit("exitDetail", true);
    },
  },
  components: {
    dialog_Articles,
    articles_catalogue,
  },
};
</script>

<style scoped>
.bg-light-soft {
  background-color: #f8fafc;
}

.border-soft {
  border: 1px solid #f1f5f9 !important;
}

.shadow-sm {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03) !important;
}

.custom-table {
  background-color: white !important;
}

.custom-table>>>th {
  background-color: #f8fafc !important;
  text-transform: uppercase;
  font-size: 0.65rem !important;
  font-weight: 800 !important;
  color: #64748b !important;
  letter-spacing: 0.05em;
  height: 40px !important;
}

.line-height-1 {
  line-height: 1 !important;
}

.max-w-300 {
  max-width: 300px;
}

.qty-field {
  max-width: 140px;
}

.qty-field>>>.v-input__control {
  min-height: 32px !important;
}

.qty-field>>>input {
  font-weight: 800;
  text-align: center;
  color: #1e293b;
}

.uppercase {
  text-transform: uppercase;
}

.italic {
  font-style: italic;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

.modern-timeline>>>.v-timeline-item__divider {
  min-width: 40px;
}

.timeline-content {
  background-color: #f8fafc;
  border-left: 2px solid #e2e8f0;
}

.text-tiny {
  font-size: 0.65rem;
}

.text-decoration-underline {
  text-decoration: underline;
}

.timeline-bubble {
  background-color: #ffffff;
  transition: all 0.3s ease;
}

.timeline-bubble:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.border-top-light {
  border-top: 1px solid #f1f5f9;
}

.shadow-xs {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.modern-timeline-refined>>>.v-timeline-item__divider {
  min-width: 48px;
}

.letter-spacing-1 {
  letter-spacing: 1px;
}

.min-h-screen {
  min-height: 90vh;
}

.h-full {
  height: 100%;
}

.custom-table >>> .invalid-item-row {
  background-color: #ffebee !important;
  border-left: 3px solid #f44336;
}
</style>
